import React, { useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  createFlightPlace,
  getAllActiveFlightPlaces,
  getAllFlightPlaces,
  getFlightPlaceByID,
  updateFlightPlace,
} from "./services/flightplace.services";
import { clearFlightPlaceDetails } from "./flightplaceSlice";
import { closed } from "../../components/drawer/drawerSlice";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import IconButton from "../../components/common/IconButton";
import { setCurrentPage } from "../pagination/paginationSlice";

const CreateFlightPlace: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const FlightPlaceDetail = useAppSelector(
    (state) => state.flightplace.FlightPlaceDetail,
  );
  const FlightPlaceID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector((state) => state.flightplace.formLoading);
  const isIndirect = useAppSelector((state) => state.drawer.isIndirect);
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const FilterFlightPlace: any = useAppSelector(
    (state) => state.flightplace.FilterFlightPlace,
  );

  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    return () => {
      dispatch(clearFlightPlaceDetails());
      dispatch(closed());
    };
  }, []);

  useEffect(() => {
    if (FlightPlaceID) {
      dispatch(getFlightPlaceByID(FlightPlaceID));
    }
  }, [FlightPlaceID]);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [FlightPlaceDetail]);

  const handleSubmit = async (values: any) => {
    try {
      if (!FlightPlaceID) {
        const insertPayload = {
          FlightPlaceName: values?.FlightPlaceName
            ? values?.FlightPlaceName
            : "",
          FlightPlaceCode: values?.FlightPlaceCode
            ? values?.FlightPlaceCode
            : "",
          AirportName: values?.AirportName ? values?.AirportName : "",
          CountryCode: values?.CountryCode ? values?.CountryCode : "",
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(createFlightPlace(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearFlightPlaceDetails());
          if (isIndirect) {
            dispatch(getAllActiveFlightPlaces());
          } else {
            dispatch(setCurrentPage(0));
            const payload = {
              ...FilterFlightPlace,
              Limit: +pageLimit,
              Skip: +currentPage * +pageLimit,
            };
            dispatch(getAllFlightPlaces(payload));
          }
        }
      } else {
        const editPayload = {
          ID: FlightPlaceID,
          FlightPlaceName: values?.FlightPlaceName
            ? values?.FlightPlaceName
            : "",
          FlightPlaceCode: values?.FlightPlaceCode
            ? values?.FlightPlaceCode
            : "",
          AirportName: values?.AirportName ? values?.AirportName : "",
          CountryCode: values?.CountryCode ? values?.CountryCode : "",
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateFlightPlace(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearFlightPlaceDetails());
          dispatch(setCurrentPage(0));
          const payload = {
            ...FilterFlightPlace,
            Limit: +pageLimit,
            Skip: +currentPage * +pageLimit,
          };
          dispatch(getAllFlightPlaces(payload));
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  return (
    <>
      {formLoading && <LoadingPanel gridRef={gridRef} />}
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={FlightPlaceDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement style={{ width: "100%" }}>
            <GridLayout
              style={{
                display: "flex",
                flexWrap: "wrap",
              }}
              gap={{ rows: 10 }}
              cols={[{ width: "100%" }]}
            >
              <GridLayoutItem className="mui-drawer-header">
                <Typography.h4 className="m-0">
                  {FlightPlaceID ? "Update Flight Place" : "Add Flight Place"}
                </Typography.h4>
                <IconButton
                  type="button"
                  fillMode={"flat"}
                  themeColor={"dark"}
                  style={{ marginRight: 4 }}
                  onClick={() => dispatch(closed())}
                  icon={RxCross1}
                />
              </GridLayoutItem>

              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="FlightPlaceName"
                  label="Flight Place"
                  placeholder="Flight Place"
                  component={FormTextField}
                  validator={requiredValidator}
                  astrike={true}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="FlightPlaceCode"
                  label="Flight Place Code"
                  placeholder="Flight Place Code"
                  astrike={true}
                  component={FormTextField}
                  validator={requiredValidator}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="AirportName"
                  label="Airport Name"
                  placeholder="Airport Name"
                  component={FormTextField}
                  validator={requiredValidator}
                  astrike={true}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="CountryCode"
                  label="Country Code"
                  placeholder="Country Code"
                  component={FormTextField}
                  validator={requiredValidator}
                  astrike={true}
                />
              </GridLayoutItem>

              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  id={"IsActive"}
                  name={"IsActive"}
                  label={"Status"}
                  component={FormSelectionField}
                  validator={requiredValidator}
                  astrike={true}
                  placeholder="i.e. Active"
                  options={STATUSARRAY?.map((status: any) => {
                    return {
                      value: status.value,
                      label: status.label,
                    };
                  })}
                />
              </GridLayoutItem>

              <GridLayoutItem
                style={{
                  textAlign: "end",
                  marginTop: "20px",
                  width: "100%",
                }}
              >
                <ButtonWithLoading
                  label={FlightPlaceID ? "Update" : "Save"}
                  type="submit"
                  disabled={!formRenderProps.allowSubmit || formLoading}
                  loading={formLoading}
                />
                <Button
                  type="button"
                  fillMode={"outline"}
                  themeColor={"primary"}
                  style={{ marginLeft: 10 }}
                  onClick={handleFormClose}
                >
                  Cancel
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateFlightPlace;
